import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Bell } from "lucide-react";
import { api } from "@/lib/api";
import Seo from "@/pages/Seo";
import SeoOverview from "@/components/seo/SeoOverview";
import SeoKeywords from "@/components/seo/SeoKeywords";
import SeoLocal from "@/components/seo/SeoLocal";
import SeoPriorities from "@/components/seo/SeoPriorities";
import SeoHistory from "@/components/seo/SeoHistory";
import SeoParams from "@/components/seo/SeoParams";

const TABS = [
  ["apercu", "Vue d'ensemble"],
  ["mots", "Mots-clés & pages"],
  ["local", "Référencement local"],
  ["priorites", "Mes 3 priorités"],
  ["suivi", "Historique & bilans"],
  ["params", "Paramètres"],
  ["audit", "Audit technique"],
];

export default function SeoHub() {
  const [tab, setTab] = useState("apercu");
  const [dueCount, setDueCount] = useState(0);
  const [params] = useSearchParams();
  useEffect(() => {
    const loadDue = () => api.get("/seo/reminders")
      .then((r) => setDueCount((r.data.reminders || []).filter((x) => x.state === "du").length))
      .catch(() => setDueCount(0));
    loadDue();
    window.addEventListener("seo-reminders-changed", loadDue);
    return () => window.removeEventListener("seo-reminders-changed", loadDue);
  }, []);
  useEffect(() => {
    if (params.get("gsc") === "connected") toast.success("Google Search Console connecté");
    if (params.get("gsc") === "error") {
      const reason = params.get("reason") || "";
      const uri = `${window.location.origin}/api/seo/gsc/oauth/callback`;
      const MESSAGES = {
        gsc_exchange: `Google a refusé l'échange du code. Vérifiez dans Google Cloud Console → votre client OAuth → « URI de redirection autorisés » que cette URI est enregistrée EXACTEMENT : ${uri}`,
        gsc_state: "Le lien de connexion a expiré ou a déjà été utilisé — cliquez à nouveau sur « Connecter Google Search Console ».",
        gsc_no_refresh: "Google n'a pas renvoyé d'autorisation durable — révoquez l'accès Photobooth54 dans myaccount.google.com/permissions puis reconnectez.",
        gsc_missing: "Réponse Google incomplète — recommencez la connexion.",
        access_denied: "Autorisation refusée côté Google — vérifiez que votre compte Gmail est bien ajouté comme utilisateur test dans Google Auth Platform.",
      };
      toast.error(MESSAGES[reason] || "Connexion Google Search Console impossible — réessayez.", { duration: 12000 });
    }
  }, [params]);
  return (
    <div className="space-y-4" data-testid="seo-hub">
      <div className="flex gap-1.5 overflow-x-auto pb-1 -mx-1 px-1" data-testid="seo-tabs">
        {TABS.map(([k, label]) => (
          <button key={k} data-testid={`seo-tab-${k}`} onClick={() => setTab(k)}
            className={`shrink-0 rounded-full px-4 py-2 text-sm font-semibold transition-colors ${tab === k ? "bg-slate-900 text-white" : "bg-white border border-slate-200 text-slate-600 hover:border-slate-400"}`}>
            {label}
            {k === "suivi" && dueCount > 0 && (
              <span data-testid="seo-tab-suivi-badge"
                className={`ml-1.5 inline-flex items-center gap-0.5 rounded-full px-1.5 py-0.5 text-[10px] font-bold ${tab === k ? "bg-amber-400 text-amber-950" : "bg-amber-500 text-white"}`}>
                <Bell className="w-3 h-3" />{dueCount}
              </span>
            )}
          </button>
        ))}
      </div>
      {tab === "apercu" && <SeoOverview goParams={() => setTab("params")} />}
      {tab === "mots" && <SeoKeywords />}
      {tab === "local" && <SeoLocal />}
      {tab === "priorites" && <SeoPriorities />}
      {tab === "suivi" && <SeoHistory />}
      {tab === "params" && <SeoParams />}
      {tab === "audit" && <Seo />}
    </div>
  );
}
